import { styled } from '@linaria/react';
import { Stack } from '@mantine/core';
import type { FloraAnnotation } from '../../../../containers/adherence/risk-analysis/risk-analysis.client.type';
import { getRiskCategory } from '../../../adherence/risk-analysis/risk-analysis.helpers';
import { useTranslation } from 'react-i18next';
import { skyDark, skyLight, skyTight } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';

type OutcomeRiskSectionProps = {
  floraAnnotations: FloraAnnotation | null | undefined;
  useAI: boolean;
  entityType: 'outcome' | 'goal';
};

export function OutcomeRiskSection({ floraAnnotations, useAI, entityType }: OutcomeRiskSectionProps) {
  const i18n = useTranslation();
  // Get the correct entity name based on type
  const entityName = entityType === 'outcome' ? i18n.t('common.outcome') : i18n.t('common.strategic_goal');

  if (!useAI) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="warning" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
            <SectionTitle>{i18n.t('strategy.overview.sections.outcome_risk', { entity: entityName })}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_risk_analysis')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  const riskScore = floraAnnotations?.risk_score;
  const executiveSummary = floraAnnotations?.executive_summary;
  const actionPlan = floraAnnotations?.action_plan;

  const riskCategory = riskScore ? getRiskCategory(riskScore) : null;

  return (
    <SectionCard>
      <Stack gap={12}>
        <SectionHeader>
          <Icon name="warning" size={18} color={skyDark} style={{ flexShrink: 0, width: '18px' }} />
          <SectionTitle>{i18n.t('strategy.overview.sections.outcome_risk', { entity: entityName })}</SectionTitle>
          {riskScore && riskCategory && (
            <RiskBadge $color={riskCategory.color}>
              <RiskScore>{riskScore.toFixed(1)}</RiskScore>
              <RiskLabel>{riskCategory.label}</RiskLabel>
            </RiskBadge>
          )}
        </SectionHeader>

        {executiveSummary ? (
          <Stack gap={16}>
            <RiskSection>
              <RiskSectionTitle>{i18n.t('strategy.overview.sections.risk_summary')}</RiskSectionTitle>
              <RiskText>{executiveSummary}</RiskText>
            </RiskSection>

            {actionPlan && (
              <RiskSection>
                <RiskSectionTitle>{i18n.t('strategy.overview.sections.recommended_actions')}</RiskSectionTitle>
                <RiskText>{actionPlan}</RiskText>
              </RiskSection>
            )}
          </Stack>
        ) : (
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('strategy.overview.empty_states.no_risk_analysis')}
            </Text>
          </EmptyState>
        )}
      </Stack>
    </SectionCard>
  );
}

const SectionCard = styled.div`
  background-color: ${skyTight};
  border-radius: 16px;
  padding: 20px;
  border: 1px solid ${skyLight};
  flex: 1;
  min-height: 200px;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const SectionTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  flex: 1;
  text-align: left;
`;

const RiskBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background-color: ${(props) => `${props.$color}20`};
  border: 1px solid ${(props) => props.$color};
`;

const RiskScore = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
`;

const RiskLabel = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${skyDark};
  text-transform: uppercase;
`;

const RiskSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RiskSectionTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${skyDark};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RiskText = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 1px dashed ${skyLight};
`;
